import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  AppBar,
  Box,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import ARichTextEditorOnlyHtml from "components/ARichTextEditor/ARichTextEditorOnlyHtml";
import { Mentionable } from "components/ARichTextEditor/useExtensions";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import PrimaryButton from "components/PrimaryButton";
import SecondaryGreyButton from "components/SecondaryGreyButton";
import { StyledContainer } from "components/StyledContainer";
import Transition from "components/Transition";
import { CommentType } from "operations/schema/schema";
import { FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppSelector } from "store";

type EditCommentDialogProps = {
  open: boolean;
  comment: CommentType | null;
  handleClose: () => void;
  handleUpdate: (comment: CommentType) => void;
};

export const EditCommentDialog: FC<EditCommentDialogProps> = (props) => {
  const { open, comment, handleClose, handleUpdate } = props;
  const intl = useIntl();
  const { users, technicians, teams } = useAppSelector((s) => s.comments.mentionables);
  const [newComment, setNewComment] = useState<string>("");
  const loading = false;

  const mentionables = useMemo(() => {
    const mentionables: Mentionable[] = [];
    if (users) {
      mentionables.push(
        ...users.map((user, index) => ({
          mentionLabel: user.fullName ?? "",
          id: user.loginName ?? index.toString(),
          type: "user" as const,
        })),
      );
    }

    if (technicians) {
      mentionables.push(
        ...technicians.map((tech) => ({
          mentionLabel: tech.name!,
          id: tech.guid,
          type: "technician" as const,
        })),
      );
    }

    if (teams) {
      mentionables.push(
        ...teams.map((team) => ({
          mentionLabel: [team.teamCode, team.teamDescription].join(" - "),
          id: team.teamGuid,
          type: "team" as const,
        })),
      );
    }
    return mentionables;
  }, [users, technicians, teams]);

  useEffect(() => {
    if (comment) setNewComment(comment.text ?? "");
  }, [comment]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      data-testid="CommentsDialog"
    >
      <BackdropPrimaryMain open={loading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="CommentsDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className="AppBar-label" paddingRight="36px">
            <FormattedMessage id="comments.editComment" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <StyledContainer>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Box>
              {!loading && (
                <ARichTextEditorOnlyHtml
                  placeholder={intl.formatMessage({ id: "comments.editACommentHelp" })}
                  content={newComment ?? ""}
                  mentions={true}
                  mentionables={mentionables}
                  onChange={(content) => {
                    setNewComment(content);
                  }}
                />
              )}
            </Box>
          </Grid>
          <Grid item>
            <PrimaryButton
              onClick={async () => {
                if (!comment) return;
                handleUpdate({
                  ...comment,
                  text: newComment,
                });
                setNewComment("");
              }}
              style={{ marginTop: 10 }}
              disabled={!comment}
            >
              <Typography>
                <FormattedMessage id="general.save" />
              </Typography>
            </PrimaryButton>

            <SecondaryGreyButton
              onClick={() => {
                handleClose();
                setNewComment("");
              }}
              style={{ marginTop: 10 }}
            >
              <Typography>
                <FormattedMessage id="general.cancel" />
              </Typography>
            </SecondaryGreyButton>
          </Grid>
        </Grid>
      </StyledContainer>
    </Dialog>
  );
};
